/**
 * The cell colour dial's look-and-feel numbers (GRUNDO #105). One object so
 * the dev playground (`/dev/szinvalaszto`) can tune them live and the result
 * can be pasted back here as the new defaults.
 */
export interface DialTuning {
  /** Base hexagon width, px. */
  itemSize: number;
  /** Centre-to-centre distance, in item widths. */
  spacing: number;
  /** Scale of the cell under the reticle (the "magnifier"). */
  magnify: number;
  /** How far from the centre the magnification fades out, in items. */
  lensWidth: number;
  /** Scale loss per item of distance beyond the lens (0–1). */
  falloff: number;
  /** Opacity of the outermost cells (0–1). */
  minOpacity: number;
  /** Outer glow radius, px. */
  glowSize: number;
  /** Outer glow strength (0–1). */
  glowStrength: number;
  /** Glass gloss strength on every cell (0–1). */
  shine: number;
  /** Duration of the glint that sweeps the centre cell on each colour change, ms. */
  glintMs: number;
  /** Extra distance between the reticle and its two nearest neighbours, px. */
  neighborGap: number;
  /** Depth of field: blur per item of distance from the centre, px. */
  blurPerStep: number;
  /** Depth of field: darkening per item of distance from the centre (0–1). */
  dimPerStep: number;
  /** Faceted (bevelled) surface strength; the glint breaks on the facet edges (0–1). */
  facets: number;
  /** Release velocity × this = how many items the dial glides on (ms). */
  inertiaMs: number;
  /** Spring pull towards the snapped cell (per 16 ms frame). */
  stiffness: number;
  /** Spring damping (per 16 ms frame, 0–1). */
  damping: number;
  /** Glassy edge width on each side, % of the dial width. */
  edgeWidth: number;
  /** Glassy edge blur, px. */
  edgeBlur: number;
  /** Reticle size relative to the magnified cell. */
  reticleScale: number;
  /** Gap at the top and bottom of the split reticle, fraction of its height. */
  reticleGap: number;
  /** Reticle rim thickness, px. */
  reticleWidth: number;
  /** Reticle glass strength: soft halo and gradient rim (0–1). */
  reticleGlass: number;
  /** A short vibration tick when a new colour arrives under the reticle (phones). */
  haptics: boolean;
  /** Colour burst behind the dial after a choice. */
  burst: boolean;
}

/** Geri's tuning from the playground (GRUNDO #105, 2026-11-05). */
export const DEFAULT_DIAL_TUNING: DialTuning = {
  itemSize: 49,
  spacing: 1.08,
  magnify: 1.7,
  lensWidth: 1.4,
  falloff: 0.16,
  minOpacity: 0.8,
  glowSize: 23,
  glowStrength: 0.55,
  shine: 0.85,
  glintMs: 1600,
  neighborGap: 16,
  blurPerStep: 0.4,
  dimPerStep: 0.12,
  facets: 0.1,
  inertiaMs: 150,
  stiffness: 0.18,
  damping: 0.65,
  edgeWidth: 21,
  edgeBlur: 5.5,
  reticleScale: 1.39,
  reticleGap: 0.45,
  reticleWidth: 3,
  reticleGlass: 0.85,
  haptics: true,
  burst: true,
};

/** Playground slider ranges: [min, max, step]. */
export const DIAL_TUNING_RANGES: Record<Exclude<keyof DialTuning, 'burst' | 'haptics'>, readonly [number, number, number]> = {
  itemSize: [36, 96, 1],
  spacing: [0.9, 1.8, 0.01],
  magnify: [1, 2.6, 0.05],
  lensWidth: [0.4, 3, 0.05],
  falloff: [0, 0.4, 0.01],
  minOpacity: [0, 1, 0.05],
  glowSize: [0, 40, 1],
  glowStrength: [0, 1, 0.05],
  shine: [0, 1, 0.05],
  glintMs: [0, 3500, 50],
  neighborGap: [0, 40, 1],
  blurPerStep: [0, 4, 0.1],
  dimPerStep: [0, 0.5, 0.01],
  facets: [0, 1, 0.05],
  inertiaMs: [0, 900, 10],
  stiffness: [0.02, 0.4, 0.01],
  damping: [0.05, 0.9, 0.01],
  edgeWidth: [0, 45, 1],
  edgeBlur: [0, 20, 0.5],
  reticleScale: [1, 1.6, 0.01],
  reticleGap: [0, 0.45, 0.01],
  reticleWidth: [1, 12, 0.5],
  reticleGlass: [0, 1, 0.05],
};
